import type {
	AppState,
	NubeSDKRuntime,
	NubeSDKRuntimeState,
	NubeSDKStubQueueEntry,
} from "@tiendanube/nube-sdk-internal-types";
import { getRuntimeReady } from "../../runtime/src/ready";
import {
	getAppInitializer,
	serializeAppData,
	serializeInitialState,
} from "./code-handler";
import { type CommandStore, createCommandStore } from "./command";
import { addWorkerEventListener } from "./worker/event-listener";
import { createWorker } from "./worker/handler";
import { flushAllTelemetry } from "./worker/handler/telemetry";
import { isTelemetryEnabled } from "./worker/handler/telemetry/env";

const NUBE_DEVTOOLS_APPLICATION_SERVER_KEY = "nube-devtools-application-server";

type DevtoolsServerData = {
	script: string;
	connected: boolean;
	appId: string;
	type: "existing" | "new";
};

function isDevtoolsEnabled(): boolean {
	return window.__NUBE_DEVTOOLS_EXTENSION__ === true;
}

function mergeDevtoolsApp(state: NubeSDKRuntimeState): NubeSDKRuntimeState {
	let data: DevtoolsServerData | null = null;
	try {
		const raw = sessionStorage.getItem(NUBE_DEVTOOLS_APPLICATION_SERVER_KEY);
		if (raw) data = JSON.parse(raw) as DevtoolsServerData;
	} catch (error) {
		console.error("Failed to load devtools application data:", error);
	}
	if (!data?.connected || !data?.appId || !data?.script) return state;

	if (data.type === "existing") {
		const existingApp = state.apps?.[data.appId];
		if (!existingApp) return state;

		return {
			...state,
			apps: {
				...state.apps,
				[data.appId]: { ...existingApp, script: data.script },
			},
		};
	}

	return {
		...state,
		apps: {
			...state.apps,
			[data.appId]: { id: data.appId, script: data.script },
		},
	};
}

const registeredWorkers = new Set<string>();

export function _resetRegisteredWorkers() {
	registeredWorkers.clear();
	attached = false;
	pageLoaded = false;
}

function register(
	runtime: NubeSDKRuntime,
	state: NubeSDKRuntimeState,
	initializer: string,
	commandStore: CommandStore,
) {
	window.__NUBE_SDK_APPS__ = window.__NUBE_SDK_APPS__ || [];
	const { apps } = state;
	const registered: Record<string, AppState> = {};

	for (const appId in apps) {
		const app = apps[appId] as AppState;
		if (app && !app.registered && !registeredWorkers.has(appId)) {
			registeredWorkers.add(appId);

			const worker = createWorker(
				serializeAppData(app),
				serializeInitialState(state),
				initializer,
			);

			addWorkerEventListener(appId, worker, runtime, commandStore, app.script);
			app.registered = true;
			registered[appId] = app;
			window.__NUBE_SDK_APPS__.push({ id: appId, worker });
		}
	}

	if (Object.keys(registered).length > 0) {
		runtime.send("register", "app:registered", () => ({
			apps: registered,
		}));

		if (pageLoaded) {
			for (const id in registered) {
				runtime.send("register", "page:loaded", undefined, id);
			}
		}
	}
}

let attached = false;
let pageLoaded = false;
let beforeunloadListenerAdded = false;

function drainStubQueue(sdk: NubeSDKRuntime) {
	const stub = window.nubeSDK;
	if (!stub || stub === sdk) return;

	const queue = Array.isArray(stub.queue) ? stub.queue : undefined;

	// Swap the stub's methods for the real SDK's BEFORE replaying. Replaying
	// dispatches events synchronously, and host listeners react by rendering
	// (checkout runs React 17, where a `setState` from outside a React event
	// renders inline). Those components read `window.nubeSDK` again, so the
	// stub must already be the real SDK by the time the first entry replays —
	// otherwise they hit the stub's queuing methods while the queue is gone.
	// Also keeps any consumer that captured `window.nubeSDK` before the drain
	// (e.g. via a hook closure) working.
	Object.assign(stub, sdk);

	if (queue) {
		// Forwarding sink instead of `undefined`: a stub *method* captured
		// before the drain still closes over `sdk.queue`, so leaving it unset
		// makes that call throw. Forwarding straight to the runtime keeps it
		// working and prevents the call from being silently dropped into a
		// queue nobody drains again.
		stub.queue = {
			push: ([method, ...args]: NubeSDKStubQueueEntry) =>
				Reflect.apply(sdk[method as "on" | "off" | "send"], sdk, args),
		};

		// Sort `on` entries to the front so a listener registered before a
		// send actually receives that send when replayed.
		queue.sort((a, b) => {
			if (a[0] === "on" && b[0] !== "on") return -1;
			if (a[0] !== "on" && b[0] === "on") return 1;
			return 0;
		});
		for (const [method, ...args] of queue) {
			Reflect.apply(sdk[method as "on" | "off" | "send"], sdk, args);
		}
	}
}

export async function attach(nube?: NubeSDKRuntime) {
	if (attached) return;
	attached = true;

	const initializerPromise = getAppInitializer();

	// Wait for the real runtime SDK to be available before wiring anything
	// up. If the register bundle ran before the runtime bundle, the only
	// thing on `window.nubeSDK` is the bootstrap stub — calling `on(...)` on
	// it would push our registration into a queue with no one to drain it.
	// `nube` is honored when provided so unit tests can inject a runtime
	// directly.
	const sdk = nube ?? (await getRuntimeReady().promise);

	// Command store: created here so it can later be shared with the
	// per-worker dispatcher. Assigned before `drainStubQueue` so the stub on
	// `window.nubeSDK` carries it via `Object.assign`. The runtime sees only
	// the `NubeSDKCommand` shape (`add`); the dispatcher uses `commandStore`
	// directly to call `get`.
	const commandStore = createCommandStore();
	sdk.command = commandStore;

	sdk.on("app:register", async (payload) => {
		const initializer = await initializerPromise;
		const nextState = isDevtoolsEnabled() ? mergeDevtoolsApp(payload) : payload;
		register(sdk, nextState, initializer, commandStore);
	});

	// Track the host's one-shot `page:loaded` so late-registered apps can be
	// caught up. Added before `drainStubQueue` so a `page:loaded` queued on the
	// bootstrap stub flips the flag as it replays.
	sdk.on("page:loaded", () => {
		pageLoaded = true;
	});

	const initializer = await initializerPromise;

	const rawState = sdk.getState();
	const state = isDevtoolsEnabled() ? mergeDevtoolsApp(rawState) : rawState;
	const hasApps = state?.apps && Object.keys(state.apps).length > 0;

	// Spawn workers BEFORE draining so they have the `runtime.on("*")`
	// forwarder attached when the queue replays `init`, custom sends, etc.
	// `state.apps` is populated either by the Bootstrap seed or by
	// `mergeDevtoolsApp` above.
	if (hasApps) register(sdk, state, initializer, commandStore);
	drainStubQueue(sdk);

	// Set up a listener to flush telemetry before the page unloads
	// This ensures any buffered telemetry data is sent before the user navigates away
	// Only add the listener if telemetry is enabled
	if (!beforeunloadListenerAdded && isTelemetryEnabled()) {
		window.addEventListener("beforeunload", () => {
			flushAllTelemetry();
		});
		beforeunloadListenerAdded = true;
	}
}
